import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';

interface ModalShellProps {
  children: React.ReactNode;
  onClose?: () => void;
  /** When true, clicking the backdrop closes the modal. Default: true. */
  dismissOnBackdrop?: boolean;
  /** When true, pressing Escape closes the modal. Default: true. */
  dismissOnEscape?: boolean;
  /** Optional max-width Tailwind class (default 2xl). */
  widthClass?: string;
  /** Optional title rendered in the header. */
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Hide the default header bar entirely. */
  hideHeader?: boolean;
  /** Extra classes for the inner panel. */
  panelClassName?: string;
}

const ModalShell: React.FC<ModalShellProps> = ({
  children,
  onClose,
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  widthClass = 'max-w-2xl',
  title,
  subtitle,
  hideHeader = false,
  panelClassName = '',
}) => {
  useEffect(() => {
    if (!dismissOnEscape || !onClose) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => { window.removeEventListener('keydown', handler); };
  }, [dismissOnEscape, onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Frosted backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/50 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.22 }}
        onClick={dismissOnBackdrop ? onClose : undefined}
      />

      {/* Panel */}
      <motion.div
        className={`relative bg-white w-full ${widthClass} max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col border border-gray-100 ${panelClassName}`}
        initial={{ opacity: 0, scale: 0.94, y: 28 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 16, transition: { duration: 0.18 } }}
        transition={{ type: 'spring', stiffness: 420, damping: 36, mass: 0.9 }}
        onClick={(e) => { e.stopPropagation(); }}
      >
        {/* Top teal accent line */}
        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-primary/50 to-transparent z-10 pointer-events-none" />

        {/* Aurora orbs */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-primary/[0.04] blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-56 h-56 rounded-full bg-violet-400/[0.04] blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none" />

        {!hideHeader && (title ?? onClose) && (
          <div className="relative z-10 px-6 py-4 border-b border-gray-100/80 flex items-center justify-between bg-white/80 backdrop-blur-sm">
            <div className="flex flex-col min-w-0 pr-4">
              {title && (
                <h3 className="font-extrabold text-[17px] text-gray-900 tracking-tight leading-snug truncate">
                  {title}
                </h3>
              )}
              {subtitle && (
                <p className="text-[10px] font-semibold tracking-widest uppercase text-primary/60 mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>
            {onClose && (
              <button
                onClick={onClose}
                aria-label="Close"
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-all duration-150"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className="relative z-10 flex-1 overflow-y-auto">
          {children}
        </div>
      </motion.div>
    </div>,
    document.body,
  );
};

export default ModalShell;
